import { v4 as uuidV4 } from 'uuid';
import { parseZonedDateTime } from '@internationalized/date';
import { differenceInMilliseconds } from 'date-fns';
import { HubRouteLocation, LegLocationTypeEnum, SegmentTypeEnum } from '@/api/apis/route';
import { getTzDateTimeString } from '@/utils/helpers';
import { RouteSegmentType } from '@/types/route';
import { CaseFacility } from '@/api/apis/case';

export const MAIN_SEGMENTS_LIST = [SegmentTypeEnum.hospital, SegmentTypeEnum.txcenter, SegmentTypeEnum.hub];

const MAIN_SEGMENTS_WEIGHT: Partial<Record<SegmentTypeEnum, number>> = {
  [SegmentTypeEnum.hospital]: 1,
  [SegmentTypeEnum.txcenter]: 2,
  [SegmentTypeEnum.hub]: 3,
};

export const getSegmentMapKey = (segment: RouteSegmentType) => `${segment.type}|${segment.id}`;

export const sortSegmentsByTime = (segments: RouteSegmentType[]): RouteSegmentType[] => {
  return segments.toSorted((a, b) => {
    const aStatusWeight = MAIN_SEGMENTS_WEIGHT[a.type];
    const bStatusWeight = MAIN_SEGMENTS_WEIGHT[b.type];

    if (typeof aStatusWeight === 'number' && typeof bStatusWeight === 'number') {
      return aStatusWeight - bStatusWeight;
    }

    const aTime = a.actualStartTime || a.actualEndTime || a.startTime || a.endTime;
    const bTime = b.actualStartTime || b.actualEndTime || b.startTime || b.endTime;

    if (aTime && bTime) {
      const aTimeUtc = parseZonedDateTime(getTzDateTimeString(aTime)).toAbsoluteString();
      const bTimeUtc = parseZonedDateTime(getTzDateTimeString(bTime)).toAbsoluteString();

      return differenceInMilliseconds(new Date(aTimeUtc), new Date(bTimeUtc)) > 0 ? 1 : -1;
    }

    return aTime && !bTime ? -1 : 1;
  });
};

export const sortSuggestedSegments = (segments: RouteSegmentType[]): RouteSegmentType[] => {
  return [
    ...sortSegmentsByTime(segments.filter((s) => MAIN_SEGMENTS_LIST.includes(s.type))),
    ...sortSegmentsByTime(segments.filter((s) => !MAIN_SEGMENTS_LIST.includes(s.type))),
  ];
};

export const getDefaultDryRunSegments = (
  donorHospital: CaseFacility,
  donorHospitalDateTimeString?: string | null,
  arrivalHub?: HubRouteLocation,
): RouteSegmentType[] => {
  return [
    {
      id: uuidV4(),
      startLocation: {
        locationId: donorHospital.id,
        locationType: LegLocationTypeEnum.hospital,
      },
      type: SegmentTypeEnum.dhToHub,
      startTime: donorHospitalDateTimeString,
      actualStartTime: donorHospitalDateTimeString,
      endLocation: {
        locationId: arrivalHub ? arrivalHub.id : '',
        locationType: LegLocationTypeEnum.hub,
      },
    } as RouteSegmentType,
  ];
};

export type GetDefaultSegmentsParams = {
  caseId?: string;
  donorHospital: CaseFacility;
  txCenter?: CaseFacility;
  nowDateTimeString?: string;
  donorHospitalDateTimeString?: string | null;
  recipientHospitalDateTimeString?: string | null;
  donorOperationEndDateTimeString?: string | null;
  recipientOperationEndDateTimeString?: string | null;
  departureHub?: HubRouteLocation;
  arrivalHub?: HubRouteLocation;
  withoutTxCenterSegmentTypes?: SegmentTypeEnum[];
};

export const getDefaultSegments = ({
  donorHospital,
  txCenter,
  donorHospitalDateTimeString,
  recipientHospitalDateTimeString,
  donorOperationEndDateTimeString,
  recipientOperationEndDateTimeString,
  departureHub,
  arrivalHub,
  withoutTxCenterSegmentTypes = [],
}: GetDefaultSegmentsParams): RouteSegmentType[] => {
  const toDonor = {
    id: uuidV4(),
    startLocation: {
      locationId: departureHub ? departureHub.id : '',
      locationType: LegLocationTypeEnum.hub,
    },
    type: SegmentTypeEnum.hospital,
    endTime: donorHospitalDateTimeString,
    actualEndTime: donorHospitalDateTimeString,
    endLocation: {
      locationId: donorHospital.id,
      locationType: LegLocationTypeEnum.hospital,
    },
  } as RouteSegmentType;

  const toTxCenter = {
    id: uuidV4(),
    startLocation: {
      locationId: donorHospital.id,
      locationType: LegLocationTypeEnum.hospital,
    },
    type: SegmentTypeEnum.txcenter,
    startTime: donorOperationEndDateTimeString,
    actualStartTime: donorOperationEndDateTimeString,
    endTime: recipientHospitalDateTimeString,
    actualEndTime: recipientHospitalDateTimeString,
    endLocation: {
      locationId: txCenter?.id || '',
      locationType: LegLocationTypeEnum.txcenter,
    },
  } as RouteSegmentType;

  const toHub = {
    id: uuidV4(),
    startLocation: {
      locationId: txCenter?.id || '',
      locationType: LegLocationTypeEnum.txcenter,
    },
    type: SegmentTypeEnum.hub,
    startTime: recipientOperationEndDateTimeString,
    actualStartTime: recipientOperationEndDateTimeString,
    endLocation: {
      locationId: arrivalHub ? arrivalHub.id : '',
      locationType: LegLocationTypeEnum.hub,
    },
  } as RouteSegmentType;

  if (txCenter) {
    return [toDonor, toTxCenter, toHub];
  }

  return [toDonor, ...[toTxCenter, toHub].filter((s) => withoutTxCenterSegmentTypes.includes(s.type))];
};
